import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Lightning, EnvelopeSimple, Lock } from '@phosphor-icons/react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate(result.data?.role === 'admin' ? '/admin' : '/');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Lightning size={28} weight="fill" className="text-[#0052FF]" />
            <span className="font-black text-xl tracking-tight text-[#0B132B]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              AIAURORA<span className="text-[#10B981]">.</span>
            </span>
          </Link>
          <h1
            className="text-2xl sm:text-3xl font-bold text-[#0B132B] tracking-tight"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Client Portal
          </h1>
          <p className="text-base text-[#475569] mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Sign in to access your enterprise dashboard
          </p>
        </div>

        {/* Form */}
        <div className="bg-white border border-slate-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-base text-red-700" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-base font-medium text-[#0B132B]">Email Address</Label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="pl-10 h-11 rounded-none border-slate-200 focus-visible:ring-[#0052FF]"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-base font-medium text-[#0B132B]">Password</Label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="pl-10 h-11 rounded-none border-slate-200 focus-visible:ring-[#0052FF]"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-none bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-base text-[#475569]">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#0052FF] font-medium hover:underline" data-testid="login-register-link">
                Register
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
